import React, { useEffect, useState } from 'react';
import { masterSettingsAPI } from '@/api/api';
import { Plus, Edit, Trash2, Search, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const Semesters = () => {
  const [semesters, setSemesters] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSemester, setCurrentSemester] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    course_id: '',
    semester_number: 1,
    entry_type: 'regular'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [semestersRes, coursesRes] = await Promise.all([
        masterSettingsAPI.getSemesters(),
        masterSettingsAPI.getCourses()
      ]);
      setSemesters(semestersRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await masterSettingsAPI.updateSemester(currentSemester.id, formData);
      } else {
        await masterSettingsAPI.createSemester(formData);
      }
      setIsDialogOpen(false);
      setEditMode(false);
      setCurrentSemester(null);
      setFormData({ name: '', course_id: '', semester_number: 1, entry_type: 'regular' });
      fetchData();
    } catch (error) {
      console.error('Error saving semester:', error);
      alert('Failed to save semester');
    }
  };

  const handleEdit = (semester) => {
    setCurrentSemester(semester);
    setFormData({
      name: semester.name,
      course_id: semester.course_id,
      semester_number: semester.semester_number,
      entry_type: semester.entry_type || 'regular'
    });
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this semester?')) {
      try {
        await masterSettingsAPI.deleteSemester(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting semester:', error);
        alert('Failed to delete semester');
      }
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'Unknown';
  };

  const getCourseCode = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.code : '-';
  };

  const filteredSemesters = semesters.filter(semester => {
    const matchesSearch = semester.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         semester.semester_number.toString().includes(searchTerm);
    const matchesCourse = selectedCourseFilter === '' || semester.course_id === selectedCourseFilter;
    return matchesSearch && matchesCourse;
  });

  // Group semesters by course
  const semestersByCourse = courses.map(course => {
    const courseSemesters = filteredSemesters.filter(s => s.course_id === course.id);
    return {
      course,
      semesters: courseSemesters.sort((a, b) => a.semester_number - b.semester_number)
    };
  }).filter(group => group.semesters.length > 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="loading">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="semesters-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Semesters Management</h1>
          <p className="text-gray-600 mt-1">Manage semesters for all courses</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditMode(false);
            setCurrentSemester(null);
            setFormData({ name: '', course_id: '', semester_number: 1 });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-semester-button">
              <Plus size={20} className="mr-2" /> Add Semester
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="semester-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Semester' : 'Add New Semester'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="course_id">Course *</Label>
                <select
                  id="course_id"
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={formData.course_id}
                  onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                  required
                  data-testid="course-select"
                >
                  <option value="">Select Course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name} ({course.code})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="semester_number">Semester Number *</Label>
                <Input
                  id="semester_number"
                  type="number"
                  min="1"
                  max="12"
                  value={formData.semester_number}
                  onChange={(e) => {
                    const num = parseInt(e.target.value);
                    setFormData({ 
                      ...formData, 
                      semester_number: num,
                      name: `Semester ${num}`
                    });
                  }}
                  required
                  data-testid="semester-number-input"
                />
              </div>

              <div>
                <Label htmlFor="name">Semester Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Semester 1, Fall 2024"
                  required
                  data-testid="semester-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Entry Type *</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="entry_regular"
                      name="entry_type"
                      value="regular"
                      checked={formData.entry_type === 'regular'}
                      onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="entry_regular" className="text-sm cursor-pointer">
                      <span className="font-medium">Regular Only</span>
                      <span className="text-xs text-gray-500 ml-2">(For students joining in 1st semester)</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="entry_lateral"
                      name="entry_type"
                      value="lateral"
                      checked={formData.entry_type === 'lateral'}
                      onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                    />
                    <label htmlFor="entry_lateral" className="text-sm cursor-pointer">
                      <span className="font-medium">Lateral Entry Only</span>
                      <span className="text-xs text-gray-500 ml-2">(For students joining in 3rd semester)</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="entry_both"
                      name="entry_type"
                      value="both"
                      checked={formData.entry_type === 'both'}
                      onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <label htmlFor="entry_both" className="text-sm cursor-pointer">
                      <span className="font-medium">Both Regular & Lateral</span>
                      <span className="text-xs text-gray-500 ml-2">(Mandatory for both types)</span>
                    </label>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-semester-button">
                {editMode ? 'Update Semester' : 'Create Semester'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <Input
              placeholder="Search semesters by name or number..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-semesters-input"
            />
          </div>
          
          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedCourseFilter}
              onChange={(e) => setSelectedCourseFilter(e.target.value)}
              data-testid="filter-course-select"
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name} ({course.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Semesters by Course */}
      <div className="space-y-6">
        {semestersByCourse.length === 0 ? (
          <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
            <BookOpen size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No semesters found. Add your first semester to get started.</p>
          </div>
        ) : (
          semestersByCourse.map(({ course, semesters: courseSemesters }) => (
            <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid={`course-group-${course.id}`}>
              {/* Course Header */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{course.name}</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Course Code: {course.code} | Duration: {course.duration_years} years
                    </p>
                  </div>
                  <div className="bg-white px-4 py-2 rounded-lg border border-blue-200">
                    <p className="text-sm text-gray-600">Total Semesters</p>
                    <p className="text-2xl font-bold text-blue-600">{courseSemesters.length}</p>
                  </div>
                </div>
              </div>

              {/* Semesters Grid */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {courseSemesters.map((semester) => (
                    <div 
                      key={semester.id} 
                      className="relative group border-2 border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all"
                      data-testid={`semester-card-${semester.id}`}
                    >
                      {/* Semester Number Badge */}
                      <div className="absolute -top-3 -left-3 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                        {semester.semester_number}
                      </div>

                      {/* Semester Content */}
                      <div className="pt-4">
                        <h3 className="font-semibold text-gray-800 mb-2">{semester.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                          <BookOpen size={16} />
                          <span>{getCourseCode(semester.course_id)}</span>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <div className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                            semester.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {semester.is_active ? 'Active' : 'Inactive'}
                          </div>
                          {semester.entry_type === 'lateral' && (
                            <div className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              Lateral Entry Only
                            </div>
                          )}
                          {semester.entry_type === 'both' && (
                            <div className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              Regular & Lateral
                            </div>
                          )}
                          {semester.entry_type === 'regular' && (
                            <div className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              Regular Only
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons - Show on Hover */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <button 
                          onClick={() => handleEdit(semester)}
                          className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                          title="Edit"
                          data-testid={`edit-semester-${semester.id}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(semester.id)}
                          className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                          title="Delete"
                          data-testid={`delete-semester-${semester.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
          <p className="text-blue-600 text-sm font-medium">Total Semesters</p>
          <p className="text-3xl font-bold text-blue-700 mt-2" data-testid="total-semesters-stat">
            {semesters.length}
          </p>
        </div>
        <div className="bg-green-50 p-6 rounded-xl border border-green-200">
          <p className="text-green-600 text-sm font-medium">Active Semesters</p>
          <p className="text-3xl font-bold text-green-700 mt-2">
            {semesters.filter(s => s.is_active).length}
          </p>
        </div>
        <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
          <p className="text-purple-600 text-sm font-medium">Courses with Semesters</p>
          <p className="text-3xl font-bold text-purple-700 mt-2">
            {new Set(semesters.map(s => s.course_id)).size}
          </p>
        </div>
        <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
          <p className="text-orange-600 text-sm font-medium">Avg Semesters/Course</p>
          <p className="text-3xl font-bold text-orange-700 mt-2">
            {courses.length > 0 ? (semesters.length / courses.length).toFixed(1) : 0}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Semesters;
